import { useState } from "react";
import { BadgeQuestionMark, Edit3Icon, EyeIcon, RotateCcw, TrashIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { Button } from "../../../Components/Buttons";
import { ToastMessage } from "../../../Components/Utils";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { handleError } from "../../../stores/components/handleError";

import payloadHelper from "../../../stores/api/helpers/payload.helper";
import { useBulkDeleteFAQsMutation, useBulkDestroyFAQsMutation, useBulkRestoreFAQsMutation, useDeleteFAQMutation, useDestroyFAQMutation, useFetchFAQsQuery, useFetchTrashedFAQsQuery, useRestoreFAQMutation } from "../../../stores/api/faqs.api";
import FAQViewModal from "./components/FAQViewModel";

const EMPTY_PAGINATION = {
    data: [],
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    current_items: 0,
    limit: 10,
};
const HEADERS = [
    { key: "section_name", label: "Section Title" },
    {
        key: "status",
        label: "Status",
        type: "status",
    },
];

const FAQList = () => {
    const navigate = useNavigate();
    const [trashed, setTrashed] = useState(false);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

    const queryParams = payloadHelper.forList({
        page,
        limit,
        search,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction,
    });

    const activeQuery = useFetchFAQsQuery(queryParams, { skip: trashed });
    const trashedQuery = useFetchTrashedFAQsQuery(queryParams, { skip: !trashed });
    const { data: faqs = EMPTY_PAGINATION, isFetching } = trashed ? trashedQuery : activeQuery;
    const [deleteFaq] = useDeleteFAQMutation();
    const [destroyFaq] = useDestroyFAQMutation();
    const [restoreFaq] = useRestoreFAQMutation();
    const [bulkDelete] = useBulkDeleteFAQsMutation();
    const [bulkDestroy] = useBulkDestroyFAQsMutation();
    const [bulkRestore] = useBulkRestoreFAQsMutation();

    const resetPage = () => setPage(1);

    const [open, setOpen] = useState(false);
    const [selectedFAQ, setSelectedFAQ] = useState(null);

    const handleSingle = (row, type = "delete") => {
        const verb = type === "restore" ? "Restore" : type === "destroy" ? "Destroy" : "Delete";
        ToastMessage({
            type: "warning",
            title: `${verb} FAQ`,
            message: `${verb} "${row.section_name}"?`,
            toastType: "confirm",
            onYes: async () => {
                try {
                    if (type === "restore") await restoreFaq(row.id).unwrap();
                    else if (type === "destroy") await destroyFaq(row.id).unwrap();
                    else await deleteFaq(row.id).unwrap();
                    ToastMessage({ type: "success", title: "Done!", message: `FAQ ${verb.toLowerCase()}d.` });
                } catch (err) {
                    handleError(err, "Failed.");
                }
            },
        });
    };

    const handleBulk = (selectedItems, type = "delete") => {
        const ids = selectedItems;
        const verb = type === "restore" ? "Restore" : type === "destroy" ? "Destroy" : "Delete";
        ToastMessage({
            type: "warning",
            title: "Bulk Action",
            message: `${verb} ${ids.length} FAQs?`,
            toastType: "confirm",
            onYes: async () => {
                try {
                    const body = payloadHelper.forBulk(ids);
                    if (type === "restore") await bulkRestore(body).unwrap();
                    else if (type === "destroy") await bulkDestroy(body).unwrap();
                    else await bulkDelete(body).unwrap();
                    ToastMessage({ type: "success", title: "Done!", message: `${ids.length} FAQs ${verb.toLowerCase()}d.` });
                } catch (err) {
                    handleError(err, "Failed bulk action.");
                }
            },
        });
    };

    const handleTrashedChange = (value) => {
        setTrashed(value);
        resetPage();
    };

    return (
        <>
            <BreadCrumb title="FAQ's" />
            <TableListing
                headers={HEADERS}
                paginationData={faqs}
                loading={isFetching}
                createButton
                createLink={ROUTE_PATHS.CREATE_FAQS}
                handleBulk={handleBulk}
                statusModule="faqs"
                search={search}
                setSearch={setSearch}
                trashed={trashed}
                setTrashed={setTrashed}
                onTrashedChange={handleTrashedChange}
                onPageChange={setPage}
                onRowsPerPageChange={(value) => {
                    setLimit(value);
                    resetPage();
                }}
                onSearch={(value) => {
                    setSearch(value);
                    resetPage();
                }}
                onSortChange={(sort) => {
                    setSortConfig(sort);
                    resetPage();
                }}
                actions={(row) => (
                    <div className="flex gap-2">
                        <Button
                            onClick={() => {
                                setSelectedFAQ(row);
                                setOpen(true);
                            }}
                            title="View FAQS"
                            icon={EyeIcon}
                            className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
                        />
                        {!trashed ? (
                            <Button
                                onClick={() => navigate(ROUTE_PATHS.CREATE_FAQS, { state: { faq: row } })}
                                title="Edit"
                                icon={Edit3Icon}
                                className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
                            />
                        ) : (
                            <Button
                                onClick={() => handleSingle(row, "restore")}
                                title="Restore"
                                icon={RotateCcw}
                                className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
                            />
                        )}
                        <Button
                            onClick={() => handleSingle(row, trashed ? "destroy" : "delete")}
                            title="Delete"
                            icon={TrashIcon}
                            className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
                        />
                    </div>
                )}

            />
            <FAQViewModal
                open={open}
                onClose={() => setOpen(false)}
                details={selectedFAQ}
            />
        </>
    );
};

export default FAQList;
